import { Inject, Injectable } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  OpeningDay,
  OpeningTimes,
  TimeRange,
  Weekday,
} from '../../../../lead.model';
import { copy } from './opening-times.utils';
import { BehaviorSubject } from 'rxjs';

export interface OpeningTimesDialogInput {
  initialOpeningTimes: OpeningTimes;
  callback: (openingTimes: OpeningTimes) => void;
}

export interface OpeningTimesOverviewInput {
  type: 'overview';
}

export interface SpecificOpeningTimesInput {
  type: 'specificOpeningTimes';
  selectedDays: Weekday[];
  wholeDayOpened: boolean;
  wholeDayClosed: boolean;
  timeRanges: TimeRange[];
}

export type CurrentComponent =
  | OpeningTimesOverviewInput
  | SpecificOpeningTimesInput;

@Injectable()
export class CurrentOpeningTimesService {
  private readonly component = new BehaviorSubject<CurrentComponent>({
    type: 'overview',
  });
  public component$ = this.component.asObservable();

  private readonly initialOpeningTimes: OpeningTimes;
  private currentOpeningTimes: OpeningTimes;

  constructor(
    @Inject(MAT_DIALOG_DATA) private readonly data: OpeningTimesDialogInput,
  ) {
    this.initialOpeningTimes = data.initialOpeningTimes;
    this.currentOpeningTimes = data.initialOpeningTimes;
  }

  getCurrentOpeningTimes(): OpeningTimes {
    return copy(this.currentOpeningTimes);
  }

  getCurrentOpeningDay(weekDay: Weekday): OpeningDay | undefined {
    return this.getCurrentOpeningTimes().openingDays.find(
      (openingDay: OpeningDay) => openingDay.weekday === weekDay,
    );
  }

  getInitialOpeningTimes(): OpeningTimes {
    return copy(this.initialOpeningTimes);
  }

  getInitialOpeningDay(weekDay: Weekday): OpeningDay | undefined {
    return this.getInitialOpeningTimes().openingDays.find(
      (openingDay: OpeningDay) => openingDay.weekday === weekDay,
    );
  }

  setOpeningTimes(openingTimes: OpeningTimes): void {
    this.currentOpeningTimes = copy(openingTimes);
    this.setCurrentComponent({ type: 'overview' });
  }

  setFinalCurrentOpeningTimes(): void {
    this.data.callback(this.currentOpeningTimes);
  }

  getSpecificOpeningTimesInput(): SpecificOpeningTimesInput {
    if (this.component.value.type === 'specificOpeningTimes') {
      return this.component.value;
    }

    throw new Error(
      'try to get Input of Specific opening times outside of component',
    );
  }

  public setCurrentComponent(currentComponent: CurrentComponent): void {
    this.component.next(currentComponent);
  }
}
